import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Save, ListChecks } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

export default function OilAdminMissions({ seasonId, onSaved }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [streak, setStreak] = useState({ target: 0, reward: 0 });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/missions`);
      setData(r.data);
      setRows(r.data.missions.map((m) => ({ ...m })));
      setStreak({ ...r.data.streak });
    } catch { toast.error('Erro ao carregar as missões.'); }
  }, [seasonId]);
  useEffect(() => { load(); }, [load]);

  const setField = (id, field, value) =>
    setRows((p) => p.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  const save = async () => {
    if (reason.trim().length < 3) { toast.error('Informe o motivo da alteração.'); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/admin/oil/season/${seasonId}/missions`, {
        missions: rows.map((m) => ({ id: m.id, target: Number(m.target), reward: Number(m.reward) })),
        streak_target: Number(streak.target), streak_reward: Number(streak.reward), reason });
      toast.success(r.data.changed
        ? `${r.data.changed} alteração(ões) salva(s) · ${fmt(r.data.daily_total)} Black Coins por dia`
        : 'Nada mudou nas missões.');
      setReason('');
      await load();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar.'); }
    setBusy(false);
  };

  if (!data) {
    return <div className="flex justify-center py-5"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;
  }

  const previewTotal = rows.reduce((s, m) => s + (Number(m.reward) || 0), 0);

  return (
    <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-3" data-testid="oil-admin-missions">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-black uppercase text-emerald-800">
        <ListChecks className="h-3.5 w-3.5" /> Recompensas das missões diárias
      </p>
      <p className="mb-3 text-[11px] leading-relaxed text-emerald-900">
        Alvo e prêmio de cada missão, em Black Coins. Vale para os resgates seguintes — o que o
        jogador já recebeu hoje continua igual.
      </p>

      <div className="space-y-2">
        {rows.map((m) => (
          <div key={m.id} className="grid items-end gap-2 sm:grid-cols-[1fr_110px_110px]"
            data-testid={`oil-mission-row-${m.id}`}>
            <div className="min-w-0">
              <p className="truncate text-xs font-bold text-slate-700">{m.title}</p>
              <p className="truncate text-[10px] text-slate-500">{m.desc}</p>
            </div>
            <label className="text-[10px] font-bold text-slate-500">
              <span className="block">Alvo</span>
              <input type="number" min="1" value={m.target}
                data-testid={`oil-mission-target-${m.id}`}
                onChange={(e) => setField(m.id, 'target', e.target.value)}
                className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
            </label>
            <label className="text-[10px] font-bold text-slate-500">
              <span className="block">Prêmio (Black Coins)</span>
              <input type="number" min="0" value={m.reward}
                data-testid={`oil-mission-reward-${m.id}`}
                onChange={(e) => setField(m.id, 'reward', e.target.value)}
                className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
            </label>
          </div>
        ))}
      </div>

      <div className="mt-3 grid items-end gap-2 sm:grid-cols-[1fr_110px_110px]" data-testid="oil-mission-row-streak">
        <div>
          <p className="text-xs font-bold text-slate-700">Bônus de sequência</p>
          <p className="text-[10px] text-slate-500">Dias seguidos de acesso para ganhar o bônus</p>
        </div>
        <label className="text-[10px] font-bold text-slate-500">
          <span className="block">Dias seguidos</span>
          <input type="number" min="2" max="60" value={streak.target}
            data-testid="oil-mission-streak-target"
            onChange={(e) => setStreak((p) => ({ ...p, target: e.target.value }))}
            className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
        </label>
        <label className="text-[10px] font-bold text-slate-500">
          <span className="block">Prêmio (Black Coins)</span>
          <input type="number" min="0" value={streak.reward}
            data-testid="oil-mission-streak-reward"
            onChange={(e) => setStreak((p) => ({ ...p, reward: e.target.value }))}
            className="mt-0.5 h-9 w-full rounded-lg border px-2 text-sm font-bold" />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-bold text-slate-600" data-testid="oil-missions-preview">
          Total possível por dia: <b className="text-emerald-700">{fmt(previewTotal)}</b> Black Coins
          <span className="ml-2 text-slate-400">· valendo agora: {fmt(data.daily_total)}</span>
        </p>
        <div className="flex flex-1 items-center justify-end gap-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="oil-missions-reason" placeholder="Motivo da alteração (obrigatório)"
            className="h-9 w-full max-w-[260px] rounded-lg border px-2 text-xs" />
          <Button size="sm" disabled={busy} onClick={save} data-testid="oil-missions-save"
            className="bg-emerald-600 text-white hover:bg-emerald-700">
            <Save className="mr-1 h-3.5 w-3.5" /> Salvar missões
          </Button>
        </div>
      </div>
    </div>
  );
}
